import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { ToastProvider } from './context/ToastContext';
import { OnboardingProvider, useOnboarding } from './context/OnboardingContext';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from 'sonner';
import { AppLayout } from './components/layout/AppLayout';
import { Landing } from './pages/Landing';
import { ControlTower } from './pages/ControlTower';
import { CaseDetail } from './pages/CaseDetail';
import { ActionBundleReview } from './pages/ActionBundleReview';
import { PolicyStudio } from './pages/PolicyStudio';
import { Identity } from './pages/Identity';
import { Endpoints } from './pages/Endpoints';
import { SaaSPage } from './pages/SaaSPage';
import { SecurityPage } from './pages/SecurityPage';
import { AgentFleet } from './pages/AgentFleet';
import { Reconciliation } from './pages/Reconciliation';
// Marketing pages
import { Pilot } from './pages/Pilot';
import { Pricing } from './pages/Pricing';
import { Integrations } from './pages/Integrations';
import { SecurityInfo } from './pages/SecurityInfo';
import { SecurityGovernance } from './pages/SecurityGovernance';
import { Demo } from './pages/Demo';
import { Workflows } from './pages/Workflows';
import { Autonomy } from './pages/Autonomy';
import { Evidence } from './pages/Evidence';
// New marketing pages
import { ProductOverview } from './pages/marketing/ProductOverview';
import { ResolveRequests } from './pages/marketing/ResolveRequests';
import { AccessManagement } from './pages/marketing/AccessManagement';
import { AutomationBuilder } from './pages/marketing/AutomationBuilder';
import { Knowledge } from './pages/marketing/Knowledge';
import { TicketingITSM } from './pages/marketing/TicketingITSM';
import { IntegrationsDirectory } from './pages/marketing/IntegrationsDirectory';
import { IntegrationDetail } from './pages/marketing/IntegrationDetail';
import { SecurityTrust } from './pages/marketing/SecurityTrust';
import { Comparisons } from './pages/marketing/Comparisons';
import { DocsLanding } from './pages/marketing/DocsLanding';
import { Templates } from './pages/marketing/Templates';
import { RequestDemo } from './pages/marketing/RequestDemo';
// Legacy pages - Evidence subsystems
import { Assets } from './pages/Assets';
import { Services } from './pages/Services';
import { Compliance } from './pages/Compliance';
import { Cost } from './pages/Cost';
import { Connectors } from './pages/Connectors';
import { Audit } from './pages/Audit';
import { Setup } from './pages/Setup';
import { Teams } from './pages/settings/Teams';
import { People } from './pages/settings/People';
import { Channels } from './pages/settings/Channels';
import { Routing } from './pages/settings/Routing';
import { Permissions } from './pages/settings/Permissions';
import { Tickets } from './pages/Tickets';
import { TicketDetail } from './pages/TicketDetail';
import { Approvals } from './pages/Approvals';
import { ApprovalDetail } from './pages/ApprovalDetail';
import { WorkflowLibrary } from './pages/WorkflowLibrary';
import { WorkflowDetail } from './pages/WorkflowDetail';
import { Guidance } from './pages/Guidance';
import { KnowledgeBase } from './pages/KnowledgeBase';
import { Suggestions } from './pages/Suggestions';
// Aha flow pages
import { Portal } from './pages/Portal';
import { MyRequests } from './pages/MyRequests';
// Legacy pages - kept for reference
import { Dashboard } from './pages/Dashboard';
import { Incidents } from './pages/Incidents';
import { ConnectorStudio } from './pages/ConnectorStudio';
import { Automation } from './pages/Automation';
import { Policies } from './pages/Policies';
import { Changes } from './pages/Changes';
import { ModuleCatalog } from './pages/ModuleCatalog';

function App() {
  return (
    <ToastProvider>
      <OnboardingProvider>
        <TooltipProvider delayDuration={300}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </OnboardingProvider>
      <Toaster richColors position="bottom-right" duration={4000} closeButton />
    </ToastProvider>
  );
}

function SetupRedirectGuard({ children }) {
  const { isSetupComplete } = useOnboarding();
  const location = useLocation();

  const isSetupPage = location.pathname === '/setup';

  const isMarketingPage = [
    '/', '/pilot', '/pricing', '/integrations', '/security-info', '/demo',
    '/workflows', '/autonomy', '/evidence', '/product', '/trust', '/comparisons', '/docs', '/templates'
  ].some(path => location.pathname === path || location.pathname.startsWith(path + '/'));

  const isAhaFlow = ['/portal', '/my-requests', '/tickets', '/approvals', '/control-tower'].some(
    path => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  const demoMode = (() => {
    try {
      const v = localStorage.getItem('vedaya_demo_mode');
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  })();

  if (!demoMode && !isSetupComplete() && !isSetupPage && !isMarketingPage && !isAhaFlow) {
    return <Navigate to="/setup" replace />;
  }

  return children;
}

// Separate component to access useLocation
function AppRoutes() {
  return (
    <Routes>
      {/* Marketing Pages - No Sidebar */}
      <Route path="/" element={<Landing />} />
      <Route path="/pilot" element={<Pilot />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/integrations" element={<IntegrationsDirectory />} />
      <Route path="/integrations/:id" element={<IntegrationDetail />} />
      <Route path="/security-info" element={<SecurityInfo />} />
      <Route path="/security-governance" element={<SecurityGovernance />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/workflows" element={<Workflows />} />
      <Route path="/autonomy" element={<Autonomy />} />
      <Route path="/evidence" element={<Evidence />} />

      {/* Product Marketing Pages */}
      <Route path="/product" element={<ProductOverview />} />
      <Route path="/product/resolve-requests" element={<ResolveRequests />} />
      <Route path="/product/access-management" element={<AccessManagement />} />
      <Route path="/product/automation-builder" element={<AutomationBuilder />} />
      <Route path="/product/knowledge" element={<Knowledge />} />
      <Route path="/product/itsm" element={<TicketingITSM />} />
      <Route path="/trust" element={<SecurityTrust />} />
      <Route path="/comparisons" element={<Comparisons />} />
      <Route path="/docs" element={<DocsLanding />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/demo/request" element={<RequestDemo />} />

      {/* Main Application - With Enterprise Layout */}
      <Route
        element={
          <SetupRedirectGuard>
            <AppLayout>
              <Outlet />
            </AppLayout>
          </SetupRedirectGuard>
        }
      >
        <Route path="/control-tower" element={<ControlTower />} />
        <Route path="/portal" element={<Portal />} />
        <Route path="/my-requests" element={<MyRequests />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tickets/:ticketId" element={<TicketDetail />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/approvals/:approvalId" element={<ApprovalDetail />} />
        <Route path="/identity" element={<Identity />} />
        <Route path="/endpoints" element={<Endpoints />} />
        <Route path="/saas" element={<SaaSPage />} />
        <Route path="/security" element={<SecurityPage />} />

        {/* Case Flow */}
        <Route path="/case/:caseId" element={<CaseDetail />} />
        <Route path="/case/:caseId/review" element={<ActionBundleReview />} />

        {/* Tools */}
        <Route path="/workflow-library" element={<WorkflowLibrary />} />
        <Route path="/workflow-library/:workflowId" element={<WorkflowDetail />} />
        <Route path="/workflow-library/:workflowId/runs" element={<WorkflowDetail />} />
        <Route path="/guidance" element={<Guidance />} />
        <Route path="/knowledge" element={<KnowledgeBase />} />
        <Route path="/suggestions" element={<Suggestions />} />
        <Route path="/policy-studio" element={<PolicyStudio />} />
        <Route path="/agents" element={<AgentFleet />} />
        <Route path="/reconciliation" element={<Reconciliation />} />

        {/* Evidence Subsystems */}
        <Route path="/assets" element={<Assets />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:serviceId" element={<Services />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/cost" element={<Cost />} />
        <Route path="/connectors" element={<Connectors />} />
        <Route path="/audit" element={<Audit />} />

        {/* Settings */}
        <Route path="/settings/teams" element={<Teams />} />
        <Route path="/settings/people" element={<People />} />
        <Route path="/settings/channels" element={<Channels />} />
        <Route path="/settings/routing" element={<Routing />} />
        <Route path="/settings/permissions" element={<Permissions />} />

        {/* Legacy Routes - Redirect to new structure */}
        <Route path="/dashboard" element={<Navigate to="/control-tower" replace />} />
        <Route path="/incidents" element={<Navigate to="/security" replace />} />
        <Route path="/policies" element={<Navigate to="/policy-studio" replace />} />

        {/* Legacy Routes - Keep for now */}
        <Route path="/connector-studio" element={<ConnectorStudio />} />
        <Route path="/automation" element={<Automation />} />
        <Route path="/changes" element={<Changes />} />
        <Route path="/module-catalog" element={<ModuleCatalog />} />
      </Route>
    </Routes>
  );
}

export default App;
